// frontend/src/pages/SharedComparePage.tsx
// A public share link, /s/:token (design 8.6; livestock compare design 8.4):
// two tank shots, or two photos of one organism, someone shared, for anyone
// with the link, logged in or not.
//
// Privacy:
// - One plain fetch (api/publicShare.ts): no apiClient, no credentials, no
//   Referer. Nothing is cached in React Query or browser storage.
// - Images only from this share's own public image route.
// - <meta name="referrer" content="no-referrer"> and <meta name="robots"
//   content="noindex"> while the page is open (index.html adds them before
//   the first request on a direct load; nginx sends the same as headers).
// - Google Analytics never starts on a share link (main.tsx, utils/analytics.ts).
// - The owner's title and the organism's names are shown as plain text, never
//   as HTML.

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import {
  ActionIcon,
  Alert,
  Anchor,
  AspectRatio,
  Box,
  Button,
  Card,
  Container,
  Group,
  SegmentedControl,
  Skeleton,
  Stack,
  Text,
  ThemeIcon,
  Title,
  VisuallyHidden,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconClockPause,
  IconLinkOff,
  IconMaximize,
  IconRefresh,
  IconWifiOff,
} from '@tabler/icons-react';
import { useParams } from 'react-router-dom';
import { fetchPublicShare, type PublicShareResult } from '@/api/publicShare';
import { useIsMobile } from '@/hooks/useIsMobile';
import type { CompareShotImage } from '@/features/livestock/CompareImage';
import {
  COMPARE_MODES,
  CompareFullScreen,
  CompareView,
  type CompareMode,
} from '@/features/livestock/CompareView';
import { gapLabel } from '@/features/livestock/compareShots';
import { NO_ZOOM, type CompareZoom } from '@/features/livestock/compareZoom';
import { formatPhotoDay } from '@/features/livestock/photoInboxFormat';
import { SHARED_BY } from '@/features/livestock/shareLinkActions';
import { organismComparisonTitle, TANK_COMPARISON_TITLE } from '@/features/livestock/shareHeadings';
import type { PublicShare, PublicShareSlot } from '@/types/shareLinks';

/** The tags the page keeps in <head> while it is open. */
export const SHARE_META_TAGS: ReadonlyArray<readonly [string, string]> = [
  ['referrer', 'no-referrer'],
  ['robots', 'noindex'],
];

/** A share is shown lined up, as its owner made it: no As taken. */
const SHARED_COMPARE_MODES = COMPARE_MODES.filter((option) => option.value !== 'taken');

const META_MARK = 'data-share-meta';
/** Until the share has loaded, the kind isn't known: the title names neither. */
const PAGE_TITLE = 'Shared comparison · Reef Data Hub';
/** Image errors re-check the link at most this often (a revoked link 404s its images). */
const RECHECK_MS = 10000;
/** Re-checks an image error starts by itself; after these, Try again does it. */
const AUTO_RECHECKS = 2;
/** The hidden heading over the notices: the share's kind isn't known there (as PAGE_TITLE). */
const FALLBACK_HEADING = 'Shared comparison';

/** The page's heading when the share has no title: "Tank comparison", "Coral comparison" and so on. */
function shareHeading(share: PublicShare): string {
  return share.kind === 'organism_compare'
    ? organismComparisonTitle(share.livestock_type)
    : TANK_COMPARISON_TITLE;
}

/** The document title once the share has loaded: "Shared coral comparison · Reef Data Hub". */
function loadedPageTitle(share: PublicShare): string {
  return `Shared ${shareHeading(share).toLowerCase()} · Reef Data Hub`;
}

/** Adds the meta tags (or keeps the ones index.html added) and removes them on leaving. */
function useShareMetaTags(): void {
  useEffect(() => {
    const added = SHARE_META_TAGS.map(([name, content]) => {
      let meta = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"][${META_MARK}]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.name = name;
        meta.setAttribute(META_MARK, '');
        document.head.appendChild(meta);
      }
      meta.content = content;
      return meta;
    });
    const previousTitle = document.title;
    document.title = PAGE_TITLE;
    return () => {
      added.forEach((meta) => meta.remove());
      document.title = previousTitle;
    };
  }, []);
}

type PageState = { status: 'loading' } | PublicShareResult;

function slotImage(slot: PublicShareSlot, fullScreen: boolean): CompareShotImage {
  // Drawn as the owner lined it up; the public framing has no reference id
  const framing = slot.framing ? { ...slot.framing, ref_photo_id: null } : null;
  return fullScreen
    ? { src: slot.images.full, placeholder: slot.images.display, date: slot.date, framing }
    : { src: slot.images.display, placeholder: slot.images.thumb, date: slot.date, framing };
}

function Notice({
  icon: Icon,
  title,
  children,
}: {
  icon: typeof IconLinkOff;
  title: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <Card withBorder radius="xl" p="xl" ta="center" data-testid="share-notice">
      <VisuallyHidden component="h1">{FALLBACK_HEADING}</VisuallyHidden>
      <Stack align="center" gap="sm">
        <ThemeIcon size={56} radius={1000} variant="light" color="teal">
          <Icon size={28} stroke={1.5} />
        </ThemeIcon>
        <Title order={2} fz={{ base: 20, sm: 24 }}>
          {title}
        </Title>
        {children}
      </Stack>
    </Card>
  );
}

export function SharedComparePage(): JSX.Element {
  const { token = '' } = useParams();
  const isMobile = useIsMobile();
  useShareMetaTags();

  const [state, setState] = useState<PageState>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);
  const [mode, setMode] = useState<CompareMode>('slider');
  const [position, setPosition] = useState(50);
  // The synced zoom, shared by the page and full screen.
  const [zoom, setZoom] = useState<CompareZoom>(NO_ZOOM);
  const [fullOpen, setFullOpen] = useState(false);
  /** Bumped when a re-check finds the share still there: the images mount again and retry. */
  const [imageEpoch, setImageEpoch] = useState(0);
  /** A re-check after an image error couldn't tell (busy, network): Try again. */
  const [imageTrouble, setImageTrouble] = useState(false);
  const lastRecheck = useRef(0);
  const autoRechecks = useRef(0);
  const mounted = useRef(true);
  useEffect(
    () => () => {
      mounted.current = false;
    },
    []
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchPublicShare(token, controller.signal)
      .then(setState)
      .catch(() => {
        // Aborted: the page left or the token changed.
      });
    return () => controller.abort();
  }, [token, attempt]);

  /**
   * After an image error: still shared, so the images try again; gone (a 404,
   * the link was stopped), so say so; can't tell, so offer Try again.
   */
  const recheck = useCallback(async (): Promise<void> => {
    const result = await fetchPublicShare(token).catch(
      (): PublicShareResult => ({ status: 'error' })
    );
    if (!mounted.current) return;
    if (result.status === 'ok') {
      setState(result);
      setImageEpoch((value) => value + 1);
      setImageTrouble(false);
    } else if (result.status === 'gone') {
      setState(result);
    } else {
      setImageTrouble(true);
    }
  }, [token]);

  const retry = (): void => {
    setState({ status: 'loading' });
    setAttempt((value) => value + 1);
  };

  /** An image that fails may mean the link was stopped: check again (quietly, a few times). */
  const onImageError = useCallback((): void => {
    const now = Date.now();
    if (now - lastRecheck.current < RECHECK_MS) return;
    if (autoRechecks.current >= AUTO_RECHECKS) {
      setImageTrouble(true);
      return;
    }
    lastRecheck.current = now;
    autoRechecks.current += 1;
    void recheck();
  }, [recheck]);

  // The kind's title once the share has loaded; the neutral one otherwise. The
  // meta tags' effect puts the visitor's previous title back on leaving.
  const documentTitle = state.status === 'ok' ? loadedPageTitle(state.share) : PAGE_TITLE;
  useEffect(() => {
    document.title = documentTitle;
  }, [documentTitle]);

  let body: JSX.Element;
  if (state.status === 'loading') {
    body = (
      <Stack gap="md" data-testid="share-loading" aria-busy>
        <Stack gap={8}>
          <Skeleton h={14} w={200} />
          <Skeleton h={28} w="70%" />
          <Skeleton h={14} w={240} />
        </Stack>
        <Group justify="space-between" wrap="nowrap">
          <Skeleton h={isMobile ? 42 : 36} w={isMobile ? 200 : 170} radius="md" />
          <Skeleton h={isMobile ? 44 : 36} w={isMobile ? 44 : 130} radius="md" />
        </Group>
        {/* The frame's shape before the photos tell it theirs (4:3, as CompareSlider). */}
        <Box w="100%" maw="calc(min(62dvh, 760px) * 4 / 3)" mx="auto">
          <AspectRatio ratio={4 / 3}>
            <Skeleton radius="md" />
          </AspectRatio>
        </Box>
      </Stack>
    );
  } else if (state.status === 'gone') {
    body = (
      <Notice icon={IconLinkOff} title="This comparison is no longer shared">
        <Text size="sm" c="dimmed" maw={360}>
          The link may have been mistyped, or its owner stopped sharing it.
        </Text>
        <Anchor href="/tools" size="sm" fw={500}>
          Go to Reef Data Hub
        </Anchor>
      </Notice>
    );
  } else if (state.status === 'busy' || state.status === 'error') {
    body = (
      <Notice
        icon={state.status === 'busy' ? IconClockPause : IconWifiOff}
        title={state.status === 'busy' ? 'Busy, try again shortly' : "This comparison didn't load"}
      >
        <Text size="sm" c="dimmed" maw={360}>
          {state.status === 'busy'
            ? 'A lot of people are looking right now.'
            : 'Check your connection and try again.'}
        </Text>
        <Button variant="light" leftSection={<IconRefresh size={16} />} onClick={retry}>
          Try again
        </Button>
      </Notice>
    );
  } else {
    const { share } = state;
    const { before, after } = share.slots;
    const isOrganism = share.kind === 'organism_compare';
    const caption = `${gapLabel(before.date, after.date)} · ${formatPhotoDay(before.date)} to ${formatPhotoDay(after.date)}`;
    const view = (fullScreen: boolean): JSX.Element => (
      <CompareView
        // A new epoch mounts new images, which load again after a failure.
        key={imageEpoch}
        mode={mode}
        pairKey={`${token}-${imageEpoch}`}
        nounTitle={isOrganism ? 'Photo' : undefined}
        before={slotImage(before, fullScreen)}
        after={slotImage(after, fullScreen)}
        fullScreen={fullScreen}
        position={position}
        onPositionChange={setPosition}
        zoom={zoom}
        onZoomChange={setZoom}
        onImageError={onImageError}
      />
    );
    body = (
      <Stack gap="md">
        <Stack gap={4}>
          <Text size="sm" c="dimmed">
            {SHARED_BY}
          </Text>
          <Title order={1} fz={{ base: 22, sm: 30 }} lh={1.2} style={{ overflowWrap: 'anywhere' }}>
            {share.title ?? shareHeading(share)}
          </Title>
          {share.kind === 'tank_compare' && share.tank_name && (
            <Text size="sm" fw={500} data-testid="share-tank-name">
              {share.tank_name}
            </Text>
          )}
          {share.kind === 'organism_compare' && share.subject_name && (
            <Text
              size="sm"
              fw={500}
              style={{ overflowWrap: 'anywhere' }}
              data-testid="share-subject-name"
            >
              {share.subject_name}
            </Text>
          )}
          {share.kind === 'organism_compare' && share.scientific_name && (
            <Text
              size="sm"
              fs="italic"
              style={{ overflowWrap: 'anywhere' }}
              data-testid="share-scientific-name"
            >
              {share.scientific_name}
            </Text>
          )}
          <Text size="sm" style={{ fontVariantNumeric: 'tabular-nums' }} data-testid="share-gap">
            <Text span fw={600}>
              {gapLabel(before.date, after.date)}
            </Text>
            <Text span c="dimmed">
              {' '}
              · {formatPhotoDay(before.date)} to {formatPhotoDay(after.date)}
            </Text>
          </Text>
        </Stack>
        <Group justify="space-between" wrap="nowrap" gap="xs">
          <SegmentedControl
            value={mode}
            onChange={(value) => setMode(value as CompareMode)}
            // As taken is the owner's view: a share shows the shots lined up
            data={SHARED_COMPARE_MODES}
            size={isMobile ? 'md' : 'sm'}
            aria-label="Compare view"
          />
          {isMobile ? (
            <ActionIcon
              variant="light"
              size={44}
              aria-label="Full screen"
              onClick={() => setFullOpen(true)}
            >
              <IconMaximize size={16} />
            </ActionIcon>
          ) : (
            <Button
              variant="light"
              leftSection={<IconMaximize size={16} />}
              onClick={() => setFullOpen(true)}
            >
              Full screen
            </Button>
          )}
        </Group>
        {view(false)}
        {imageTrouble && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="gray"
            data-testid="share-image-trouble"
          >
            <Group gap="sm">
              <Text size="sm">Some photos didn&apos;t load.</Text>
              <Button
                size="compact-sm"
                variant="light"
                h={isMobile ? 44 : undefined}
                onClick={() => {
                  setImageTrouble(false);
                  void recheck();
                }}
              >
                Try again
              </Button>
            </Group>
          </Alert>
        )}
        <Text size="sm" c="dimmed" ta="center">
          Track your reef with{' '}
          {/* A full page load: the share page never shares a document with analytics. */}
          <Anchor href="/tools" inherit>
            Reef Data Hub
          </Anchor>
        </Text>
        <CompareFullScreen opened={fullOpen} onClose={() => setFullOpen(false)} caption={caption}>
          {view(true)}
        </CompareFullScreen>
      </Stack>
    );
  }

  return (
    <Container size="lg" py={{ base: 'md', sm: 'xl' }} px={{ base: 'md', sm: 'md' }}>
      {body}
    </Container>
  );
}
